import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { BrandMark, CocoaPod } from '../components/ui';

const TruckLogin: React.FC = () => {
  const { login, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (!authLoading && user) {
      if (user.role === 'driver') {
        navigate('/truck-portal', { replace: true });
      } else if (user.role === 'admin' || user.role === 'finance') {
        navigate('/dashboard', { replace: true });
      } else if (user.role === 'farmer') {
        navigate('/farmer-dashboard', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [user, authLoading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const loggedInUser = await login(identifier, password);
      if (loggedInUser.role !== 'driver') {
        setError('This portal is for drivers only. Use the staff login instead.');
        setLoading(false);
        return;
      }
    } catch (err: any) {
      setError(err.message || 'Invalid credentials. Please try again.');
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-ink-50 dark:bg-ink-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <BrandMark size="lg" accent="cargo" />
          <p className="text-sm text-ink-500 dark:text-ink-400">Loading…</p>
        </div>
      </div>
    );
  }

  if (user) return null;

  return (
    <div className="min-h-screen md:grid md:grid-cols-2 bg-ink-50 dark:bg-ink-950">
      {/* Brand panel */}
      <aside className="relative hidden md:flex flex-col justify-between p-10 lg:p-14 overflow-hidden brand-surface-cargo text-white">
        <div className="absolute inset-0 bg-dotted opacity-10" />
        <div className="relative z-10 flex items-center gap-3">
          <CocoaPod className="w-7 h-7" />
          <div>
            <p className="text-sm font-semibold tracking-tight">CoConnect</p>
            <p className="text-xs text-white/60">Driver & Fleet Portal</p>
          </div>
        </div>

        <div className="relative z-10 max-w-md">
          <p className="text-2xs font-semibold uppercase tracking-micro text-white/50 mb-3">For Truck Drivers</p>
          <h1 className="text-4xl lg:text-5xl font-semibold tracking-tightest leading-tight text-balance">
            Every dispatch,<br />tracked end-to-end.
          </h1>
          <p className="mt-5 text-base text-white/70 leading-relaxed text-pretty">
            Receive dispatches, navigate to collection points, log GPS in real time, confirm deliveries with photo proof, and record maintenance — all from your phone, even offline.
          </p>
          <div className="mt-10 grid grid-cols-2 gap-6">
            {[
              { k: 'GPS', l: 'Live + offline queue' },
              { k: 'Photo', l: 'Delivery proof' },
              { k: 'Auto', l: 'Odometer logging' },
              { k: 'PWA', l: 'Installable on phone' },
            ].map(s => (
              <div key={s.l}>
                <p className="text-xl font-semibold nums">{s.k}</p>
                <p className="text-xs text-white/50 mt-0.5">{s.l}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 flex items-center gap-2 text-xs text-white/40">
          <span>© 2026 HEFARCAM</span>
          <span aria-hidden>·</span>
          <span>Driver Portal</span>
        </div>
      </aside>

      {/* Form panel */}
      <main className="flex flex-col min-h-screen md:min-h-0">
        <div className="md:hidden flex items-center gap-3 p-5 safe-top">
          <BrandMark size="md" accent="cargo" />
          <div>
            <p className="text-sm font-semibold text-ink-900 dark:text-ink-50">CoConnect</p>
            <p className="text-xs text-ink-500 dark:text-ink-400">Driver Portal</p>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center p-5 md:p-10">
          <div className="w-full max-w-sm">
            <div className="mb-8">
              <p className="section-eyebrow mb-2">Driver Sign-in</p>
              <h2 className="text-2xl font-semibold text-ink-900 dark:text-ink-50 tracking-tight">Welcome back</h2>
              <p className="text-sm text-ink-500 dark:text-ink-400 mt-1.5">Sign in to view your assigned dispatches and start tracking.</p>
            </div>

            {error && (
              <div className="mb-5 px-3.5 py-2.5 rounded-lg bg-danger-50 dark:bg-danger-500/10 border border-danger-500/20 text-sm text-danger-700 dark:text-danger-400 flex items-start gap-2">
                <span aria-hidden className="mt-0.5">×</span>
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="d-identifier" className="label">Phone or email</label>
                <input
                  id="d-identifier"
                  type="text"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  required
                  autoComplete="username"
                  className="input"
                  placeholder="+2376XXXXXXXX  or  driver@hefarcam.cm"
                />
              </div>

              <div>
                <label htmlFor="d-password" className="label">Password</label>
                <div className="relative">
                  <input
                    id="d-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    className="input pr-11"
                    placeholder="••••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 btn-icon-sm text-ink-400 dark:text-ink-500"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 3l18 18M10.6 10.6a2 2 0 002.8 2.8M9.4 5.1A9.5 9.5 0 0112 5c5 0 9 4.5 10 7-.5 1.4-1.6 3.2-3.2 4.7M6.1 6.1C3.9 7.4 2.4 9.4 2 12c1 2.5 5 7 10 7 1.4 0 2.7-.3 3.9-.8" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-accent w-full py-3 mt-2 brand-surface-cargo"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none" aria-hidden>
                      <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                      <path className="opacity-90" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    <span>Signing in…</span>
                  </>
                ) : (
                  <>
                    <span>Start my shift</span>
                    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14M13 6l6 6-6 6" />
                    </svg>
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 pt-5 border-t border-ink-200 dark:border-ink-800 text-center">
              <button
                onClick={() => navigate('/login')}
                className="text-xs text-ink-500 dark:text-ink-400 hover:text-ink-900 dark:hover:text-ink-100 transition-colors"
              >
                ← Switch to staff login
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TruckLogin;
